/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import ConfirmModal from "../../../../components/Common/ConfirmModal";
import WorkerMiniProfile from "../../../../components/WorkerMiniProfile/WorkerMiniProfile";
import { mediaUrl } from "../../../../helpers/mediaHelper";
import ShiftSection from "../components/ShiftSection";
import WorkerDateCheckboxes from "../components/WorkerDateCheckboxes";
import {
  getStep1ActionKey,
  getWorkerAvailableDates,
  getWorkerDisplayName,
  getWorkerSlug,
} from "../step1/useStep1SuggestedWorkers";
import {
  getAcceptedDatesFromRequest,
  getRequestShiftDates,
  getRequestWorkerSlug,
  parseWorkerRequestsPage,
  useStep3Review,
} from "./useStep3Review";

function areSameDateSelections(a = [], b = []) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((x, i) => x === sb[i]);
}

function parseFinalInviteExpireAt(raw) {
  if (!raw) return null;
  const txt = String(raw).trim();
  const m = txt.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\s*PKT)?$/i
  );
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss] = m;
  // PKT is UTC+5.
  const utcMs =
    Date.UTC(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss)
    ) -
    5 * 60 * 60 * 1000;
  return new Date(utcMs);
}

function flattenWorkerRequestPages(query) {
  if (!query.data?.pages?.length) return [];
  return query.data.pages.flatMap((p) => parseWorkerRequestsPage(p).items);
}

function FinalInviteDraftPanel({
  patternsLen,
  finalDraftByShift,
  onRemove,
  removeDisabled,
}) {
  const hasAny = Object.values(finalDraftByShift || {}).some(
    (w) => (w?.length ?? 0) > 0
  );
  if (!hasAny) {
    return (
      <div className="border rounded p-3 mb-4 bg-light">
        <div className="fw-semibold mb-2">Final invite</div>
        <p className="text-muted small mb-0">
          Workers you add from suggested remaining or previously rejected lists
          appear here. Select dates before adding.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded p-3 mb-4 bg-light">
      <div className="fw-semibold mb-2">Final invite draft</div>
      {Array.from({ length: patternsLen }, (_, shiftIndex) => {
        const workers = finalDraftByShift[shiftIndex] || [];
        if (!workers.length) return null;
        return (
          <div key={shiftIndex} className="mb-3">
            <div className="small text-muted mb-1">Shift {shiftIndex + 1}</div>
            <ul className="list-unstyled mb-0">
              {workers.map((w) => (
                <li
                  key={w.workerSlug}
                  className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom"
                >
                  <div>
                    <span className="fw-medium">{w.workerSlug}</span>
                    {w.availableDates?.length > 0 && (
                      <div className="small text-muted">
                        {w.availableDates
                          .map((d) => dayjs(d).format("MMM D, YYYY"))
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <Button
                    color="link"
                    size="sm"
                    className="text-danger p-0"
                    disabled={removeDisabled}
                    onClick={() => onRemove(shiftIndex, w.workerSlug)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function SuggestedRemainingWorkerRow({
  worker,
  selectedDates,
  onToggleDate,
  onSelectAllDatesForWorker,
  onAddToFinalDraft,
  onRejectFull,
  addDisabled,
  addPending,
  rejectDisabled,
  rejectPending,
  idPrefix,
  onViewProfile,
}) {
  const slug = getWorkerSlug(worker);
  const name = getWorkerDisplayName(worker);
  const dates = getWorkerAvailableDates(worker);
  const photoRaw =
    worker?.uploadSelfie ||
    worker?.personalInformation?.uploadSelfie ||
    worker?.user?.photo ||
    worker?.photo;
  const photoSrc = photoRaw ? mediaUrl(photoRaw) : null;
  const rating = worker?.rating ?? worker?.averageRating;
  const distanceKm = worker?.distanceKm ?? worker?.distance;

  return (
    <div className="border rounded mb-3 overflow-hidden bg-white">
      <div className="px-3 py-3 border-bottom">
        <Row className="align-items-start g-3">
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div>
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt=""
                    className="rounded-circle"
                    style={{ width: 40, height: 40, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                  >
                    <i
                      className="mdi mdi-account text-muted"
                      style={{ fontSize: 24 }}
                    />
                  </div>
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="fw-semibold">{name}</span>
                {rating != null && (
                  <Badge color="light" className="text-dark">
                    <i className="mdi mdi-star text-warning me-1" />
                    {Number(rating).toFixed(1)}
                  </Badge>
                )}
                {distanceKm != null && (
                  <span className="text-muted small">
                    {Number(distanceKm).toFixed(1)} km
                  </span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12}>
            <div className="small fw-semibold mb-1">Available dates</div>
            <WorkerDateCheckboxes
              dates={dates}
              selectedDates={selectedDates}
              onToggleDate={(iso) => onToggleDate(iso)}
              idPrefix={idPrefix}
            />
            <Button
              color="light"
              size="sm"
              className="mt-2"
              disabled={!dates.length}
              onClick={() => onSelectAllDatesForWorker()}
            >
              Select all dates
            </Button>
          </Col>
          <Col
            xs={12}
            className="w-full d-flex gap-2 align-items-center align-items-md-end"
          >
            <Button
              color="primary"
              outline
              className="flex-grow-1"
              disabled={addDisabled}
              onClick={() =>
                slug && onAddToFinalDraft(slug, [...selectedDates])
              }
            >
              {addPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Adding…
                </>
              ) : (
                "Add to final invite"
              )}
            </Button>
            <Button
              color="danger"
              outline
              className="flex-grow-1"
              disabled={rejectDisabled || !slug}
              onClick={() => slug && onRejectFull(slug)}
            >
              {rejectPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Rejecting…
                </>
              ) : (
                "Reject"
              )}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="p-3">
        <Button
          color="link"
          size="sm"
          className="p-0 fw-semibold"
          onClick={() =>
            onViewProfile?.({ ...worker, ...worker?.user, worker: worker })
          }
        >
          View profile
        </Button>
      </div>
    </div>
  );
}

function RequestWorkerHeader({ request }) {
  const slug = getRequestWorkerSlug(request);
  const name = getWorkerDisplayName({
    ...request?.worker,
    user: request?.user,
  });
  const photoRaw =
    request?.user?.photo || request?.worker?.personalInformation?.uploadSelfie;
  const photoSrc = photoRaw ? mediaUrl(photoRaw) : null;
  const rating = request?.worker?.personalInformation?.ratingsAverage;

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
      {photoSrc ? (
        <img
          src={photoSrc}
          alt=""
          className="rounded-circle"
          style={{ width: 40, height: 40, objectFit: "cover" }}
        />
      ) : (
        <div
          className="rounded-circle bg-light d-flex align-items-center justify-content-center"
          style={{ width: 40, height: 40 }}
        >
          <i className="mdi mdi-account text-muted" style={{ fontSize: 24 }} />
        </div>
      )}
      <div>
        <span className="fw-semibold">{name}</span>
        <div className="small text-muted">
          {slug} · Shift {(request?.shiftIndex ?? 0) + 1} ·{" "}
          {request?.role?.title ?? "Role"}
        </div>
        {rating != null && (
          <Badge color="light" className="text-dark mt-1">
            <i className="mdi mdi-star text-warning me-1" />
            {Number(rating).toFixed(1)}
          </Badge>
        )}
      </div>
    </div>
  );
}

function AcceptedRequestRow({
  request,
  listBusy,
  onReject,
  pendingRejectKey,
  onViewProfile,
}) {
  const slug = getRequestWorkerSlug(request);
  const shiftIndex = request?.shiftIndex ?? 0;
  const acceptedDates = getAcceptedDatesFromRequest(request);
  const [selectedReject, setSelectedReject] = useState([]);

  const toggleReject = (iso) => {
    setSelectedReject((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso]
    );
  };

  const actionKey = slug ? getStep1ActionKey(shiftIndex, slug) : "";
  const rejectPending = !!slug && pendingRejectKey === actionKey;
  const [fullShiftRejectConfirmOpen, setFullShiftRejectConfirmOpen] =
    useState(false);

  return (
    <div className="border rounded mb-3 p-3 bg-white">
      <RequestWorkerHeader request={request} />
      <Badge color="success" className="mb-2">
        Accepted
      </Badge>
      {acceptedDates.length > 0 ? (
        <>
          <div className="small fw-semibold mb-1">
            Reject specific accepted dates
          </div>
          <WorkerDateCheckboxes
            dates={acceptedDates}
            selectedDates={selectedReject}
            onToggleDate={toggleReject}
            idPrefix={`rej-acc-${request?.id}`}
          />
          <div className="d-flex flex-wrap gap-2 mt-2">
            <Button
              color="danger"
              outline
              size="sm"
              disabled={listBusy || !selectedReject.length || rejectPending}
              onClick={() =>
                onReject(shiftIndex, slug, {
                  rejectScope: "selected_dates",
                  dates: selectedReject,
                })
              }
            >
              {rejectPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Rejecting…
                </>
              ) : (
                "Reject selected dates"
              )}
            </Button>
            <Button
              color="danger"
              size="sm"
              disabled={listBusy || rejectPending}
              onClick={() => setFullShiftRejectConfirmOpen(true)}
            >
              {rejectPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Rejecting…
                </>
              ) : (
                "Reject entire shift"
              )}
            </Button>
          </div>
        </>
      ) : (
        <p className="text-muted small mb-2">
          No accepted dates listed for this request.
        </p>
      )}
      <Button
        color="link"
        size="sm"
        className="p-0 mt-2 fw-semibold"
        onClick={() =>
          onViewProfile?.({
            ...request?.worker,
            ...request?.user,
            worker: request?.worker,
          })
        }
      >
        View profile
      </Button>

      <ConfirmModal
        isOpen={fullShiftRejectConfirmOpen}
        toggle={() => setFullShiftRejectConfirmOpen(false)}
        title="Reject entire shift?"
        message="Fully reject this worker for this shift? This applies to all dates for this invite."
        confirmLabel="Reject entire shift"
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={() => {
          setFullShiftRejectConfirmOpen(false);
          onReject(shiftIndex, slug, { rejectScope: "full_shift" });
        }}
      />
    </div>
  );
}

function PreviouslyRejectedRequestRow({
  request,
  listBusy,
  onReject,
  onAddToFinalDraft,
  pendingRejectKey,
  draftedDates = [],
  onViewProfile,
}) {
  const slug = getRequestWorkerSlug(request);
  const shiftIndex = request?.shiftIndex ?? 0;
  const dates = getRequestShiftDates(request);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const incoming = Array.isArray(draftedDates) ? draftedDates : [];
    setSelected((prev) => {
      if (
        prev.length === incoming.length &&
        prev.every((x, i) => x === incoming[i])
      ) {
        return prev;
      }
      return [...incoming];
    });
  }, [draftedDates]);

  const toggle = (iso) => {
    setSelected((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso]
    );
  };

  const actionKey = slug ? getStep1ActionKey(shiftIndex, slug) : "";
  const rejectPending = !!slug && pendingRejectKey === actionKey;
  const unchangedVsDraft = areSameDateSelections(selected, draftedDates);
  const [fullShiftRejectConfirmOpen, setFullShiftRejectConfirmOpen] =
    useState(false);

  return (
    <div className="border rounded mb-3 p-3 bg-white">
      <RequestWorkerHeader request={request} />
      <Badge color="warning" className="mb-2 text-dark">
        Previously rejected
      </Badge>
      <div className="small fw-semibold mb-1">Dates you can re-invite</div>
      <WorkerDateCheckboxes
        dates={dates}
        selectedDates={selected}
        onToggleDate={toggle}
        idPrefix={`prev-${request?.id}`}
      />
      <div className="d-flex flex-wrap gap-2 mt-2">
        <Button
          color="primary"
          outline
          size="sm"
          disabled={
            listBusy ||
            rejectPending ||
            !selected.length ||
            (draftedDates.length > 0 && unchangedVsDraft)
          }
          onClick={() =>
            slug && onAddToFinalDraft(shiftIndex, slug, [...selected])
          }
        >
          Add to final invite
        </Button>
        <Button
          color="danger"
          outline
          size="sm"
          disabled={listBusy || rejectPending}
          onClick={() => setFullShiftRejectConfirmOpen(true)}
        >
          {rejectPending ? (
            <>
              <Spinner size="sm" className="me-1" /> Rejecting…
            </>
          ) : (
            "Reject entire shift"
          )}
        </Button>
      </div>
      <Button
        color="link"
        size="sm"
        className="p-0 mt-2 fw-semibold"
        onClick={() =>
          onViewProfile?.({
            ...request?.worker,
            ...request?.user,
            worker: request?.worker,
          })
        }
      >
        View profile
      </Button>

      <ConfirmModal
        isOpen={fullShiftRejectConfirmOpen}
        toggle={() => setFullShiftRejectConfirmOpen(false)}
        title="Reject entire shift?"
        message="Fully reject this worker again for this shift?"
        confirmLabel="Reject entire shift"
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={() => {
          setFullShiftRejectConfirmOpen(false);
          onReject(shiftIndex, slug, { rejectScope: "full_shift" });
        }}
      />
    </div>
  );
}

function ReadOnlyRequestRow({ request, tone, onViewProfile }) {
  const ws = request?.workerShift;

  const decisionReasonLabel = {
    rejectedByClient: "Rejected by you",
    timeout: "Did not respond",
    rejected: "Rejected",
  };

  return (
    <div className="border rounded mb-3 p-3 bg-white">
      <RequestWorkerHeader request={request} />
      <Badge
        color={tone === "muted" ? "secondary" : "light"}
        className="mb-2 text-dark text-uppercase"
      >
        {request?.status ?? "—"}
      </Badge>
      {Array.isArray(ws) && ws.length > 0 && (
        <ul className="list-unstyled small text-muted mb-0">
          {ws.map((d) => (
            <li key={`${request?.id}-${d.date}`}>
              {dayjs(d.date).format("MMM D, YYYY")} — {d.status}
              {d.decisionReason
                ? ` (${decisionReasonLabel[d.decisionReason]})`
                : ""}
            </li>
          ))}
        </ul>
      )}
      <Button
        color="link"
        size="sm"
        className="p-0 mt-2 fw-semibold"
        onClick={() =>
          onViewProfile?.({
            ...request?.worker,
            ...request?.user,
            worker: request?.worker,
          })
        }
      >
        View profile
      </Button>
    </div>
  );
}

function WorkerRequestsSection({
  title,
  description,
  query,
  renderRow,
  emptyMessage,
}) {
  const items = flattenWorkerRequestPages(query);
  const loading = query.isLoading;
  const err = query.isError;

  return (
    <div className="mb-4">
      <h5 className="mb-1">{title}</h5>
      <p className="text-muted small mb-3">{description}</p>
      {err && (
        <Alert color="danger" className="py-2">
          Could not load this list.
        </Alert>
      )}
      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner size="sm" />
          <span className="text-muted small">Loading…</span>
        </div>
      )}
      {!loading && !err && !items.length && (
        <p className="text-muted small mb-0">{emptyMessage}</p>
      )}
      {!loading && !!items.length && (
        <Row className="g-3">
          {items.map((req, idx) => (
            <Col
              key={req?.id || `${req?.workerSlug || "worker"}-${idx}`}
              xs={12}
              md={6}
              xl={4}
            >
              {renderRow(req)}
            </Col>
          ))}
        </Row>
      )}
      {!loading && query.hasNextPage && (
        <Button
          color="secondary"
          outline
          size="sm"
          className="mt-2"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? (
            <>
              <Spinner size="sm" className="me-1" /> Loading…
            </>
          ) : (
            "Load more"
          )}
        </Button>
      )}
    </div>
  );
}

export default function Step3Review({ booking, bookingSlug, refetchBooking }) {
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const {
    byShift,
    selected,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToFinalDraft,
    finalDraftByShift,
    removeFromFinalDraft,
    rejectWorker,
    pendingRejectKey,
    listBusy,
    inviteMutation,
    canSendFinalInvites,
    sendFinalInvites,
    overCapDateIssues,
    inviteConfirmOpen,
    setInviteConfirmOpen,
    acceptedQ,
    prevRejectedQ,
    rejectedQ,
    invitedQ,
    isFinalInvited,
    requestInvoice,
    requestInvoiceMutation,
    requestInvoiceEligibleByAcceptance,
  } = useStep3Review({ booking, bookingSlug, refetchBooking });

  const patterns = booking?.shiftPattern ?? [];
  const finalInviteExpireDate = parseFinalInviteExpireAt(
    booking?.finalInviteExpireAt
  );
  const finalInviteExpired =
    finalInviteExpireDate instanceof Date &&
    !Number.isNaN(finalInviteExpireDate.getTime()) &&
    Date.now() > finalInviteExpireDate.getTime();
  const canShowRequestInvoice =
    (isFinalInvited && finalInviteExpired) ||
    requestInvoiceEligibleByAcceptance;
  const invoices = Array.isArray(booking?.invoiceInfo) ? booking.invoiceInfo : [];
  const hasAnyUnpaidInvoice = invoices.some(
    (invoice) => Boolean(invoice?.invoiceId) && invoice?.isPaid !== true
  );
  const areAllInvoicesPaid = invoices.length > 0 && !hasAnyUnpaidInvoice;
  const isInvoiceAlreadyRequested = booking?.requestInvoice === true;
  const openProfile = (user) => {
    setProfileUser(user);
    setIsProfileOpen(true);
  };

  if (!patterns.length) {
    return <Alert color="warning">This booking has no shift patterns.</Alert>;
  }

  return (
    <>
      {isFinalInvited ? (
        <Alert color="success" className="mb-3">
          Final invites have already been sent for this booking.
          Remaining-suggestions and rejected-history sections are now hidden.
        </Alert>
      ) : (
        <Alert color="info" className="mb-3">
          Smart matching is complete. Review suggested remaining workers, manage
          responses below, then send your <strong>final</strong> manual invites.
          You can still reject workers or add workers with selected dates until
          you confirm.
        </Alert>
      )}

      {!isFinalInvited && (
        <FinalInviteDraftPanel
          patternsLen={patterns.length}
          finalDraftByShift={finalDraftByShift}
          onRemove={removeFromFinalDraft}
          removeDisabled={listBusy || inviteMutation.isPending}
        />
      )}
      {!isFinalInvited && overCapDateIssues.length > 0 && (
        <Alert color="warning" className="mb-3">
          You have reached the maximum invite limit for one or more shift dates.
          Update the selected dates before inviting more workers.
        </Alert>
      )}

      {!isFinalInvited && <h4 className="mb-3">Suggested remaining workers</h4>}
      {!isFinalInvited &&
        patterns.map((pattern, shiftIndex) => {
          const row = byShift[shiftIndex] || {};
          const workersAll = row.workers ?? [];
          const hasMore = row.hasMore;
          const matchingStatus = row.matchingStatus;
          const sessionError = row.sessionError;
          const suggestedFetchError = row.suggestedFetchError;
          const initialLoaded = row.initialLoaded;
          const loadingInit =
            initialPending[shiftIndex] === true &&
            !initialLoaded &&
            !sessionError &&
            !suggestedFetchError;
          const lm = loadMorePending[shiftIndex];
          const selRow = selected[shiftIndex] || {};
          const shiftHasOverCapIssue = overCapDateIssues.some(
            (issue) => issue.shiftIndex === shiftIndex
          );

          return (
            <ShiftSection
              key={shiftIndex}
              shiftIndex={shiftIndex}
              pattern={pattern}
            >
              {matchingStatus === "processing" && !workersAll.length && (
                <Alert color="info" className="d-flex align-items-center gap-2">
                  <Spinner size="sm" /> Finding workers…
                </Alert>
              )}

              {sessionError && (
                <Alert color="danger">
                  Could not load your saved suggested-remaining session. Refresh
                  the page or contact support if this continues.
                </Alert>
              )}

              {suggestedFetchError && !sessionError && (
                <Alert color="danger">
                  Could not load suggested remaining workers for this shift.
                </Alert>
              )}

              {shiftHasOverCapIssue && (
                <Alert color="warning" className="py-2">
                  This shift has reached its date-based invite limit. Deselect a
                  few dates or remove workers from the final draft to continue.
                </Alert>
              )}

              {loadingInit &&
                !workersAll.length &&
                matchingStatus !== "processing" && (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                )}

              {!loadingInit &&
                !workersAll.length &&
                matchingStatus !== "processing" &&
                !sessionError &&
                !suggestedFetchError && (
                  <p className="text-muted mb-0">
                    No suggested remaining workers for this shift.
                  </p>
                )}

              {!!workersAll.length && (
                <Row className="g-3">
                  {workersAll.map((worker, wi) => {
                    const slug = getWorkerSlug(worker);
                    const draftedEntry = slug
                      ? (finalDraftByShift[shiftIndex] || []).find(
                          (x) => x.workerSlug === slug
                        )
                      : null;
                    const draftDates = draftedEntry?.availableDates || [];
                    const hasLocalSelection =
                      !!slug &&
                      Object.prototype.hasOwnProperty.call(selRow, slug);
                    const selectedDates =
                      (hasLocalSelection ? selRow[slug] : draftDates) || [];
                    const unchangedVsDraft = areSameDateSelections(
                      selectedDates,
                      draftDates
                    );
                    const actionKey = slug
                      ? getStep1ActionKey(shiftIndex, slug)
                      : "";
                    return (
                      <Col key={slug || wi} xs={12} md={6} xl={4}>
                        <SuggestedRemainingWorkerRow
                          worker={worker}
                          selectedDates={selectedDates}
                          onToggleDate={(iso) =>
                            slug && toggleDate(shiftIndex, slug, iso)
                          }
                          onSelectAllDatesForWorker={() =>
                            slug && selectAllDatesForWorker(shiftIndex, slug)
                          }
                          onAddToFinalDraft={(s, dates) =>
                            addToFinalDraft(shiftIndex, s, dates)
                          }
                          onRejectFull={(s) =>
                            rejectWorker(shiftIndex, s, {
                              rejectScope: "full_shift",
                            })
                          }
                          addDisabled={
                            listBusy ||
                            !slug ||
                            !selectedDates.length ||
                            (draftDates.length > 0 && unchangedVsDraft)
                          }
                          addPending={false}
                          rejectDisabled={
                            listBusy || !slug || draftDates.length > 0
                          }
                          rejectPending={
                            !!slug && pendingRejectKey === actionKey
                          }
                          idPrefix={`r${shiftIndex}-w${wi}`}
                          onViewProfile={openProfile}
                        />
                      </Col>
                    );
                  })}
                </Row>
              )}

              <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
                {hasMore && workersAll.length > 0 && (
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    disabled={lm}
                    onClick={() => loadMore(shiftIndex)}
                  >
                    {lm ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Loading…
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                )}
              </div>
            </ShiftSection>
          );
        })}

      {!isFinalInvited && <hr className="my-4" />}

      <h4 className="mb-3">Booking requests</h4>

      {canShowRequestInvoice && (
        <div className="d-grid d-md-flex justify-content-md-end mb-3">
          <Button
            color="primary"
            disabled={
              areAllInvoicesPaid ||
              (!hasAnyUnpaidInvoice &&
                !areAllInvoicesPaid &&
                (isInvoiceAlreadyRequested || requestInvoiceMutation.isPending))
            }
            onClick={() => {
              if (hasAnyUnpaidInvoice) {
                navigate(`/client/bookings/${bookingSlug}/payment`);
                return;
              }
              requestInvoice();
            }}
          >
            {hasAnyUnpaidInvoice ? (
              "Proceed to Payment"
            ) : areAllInvoicesPaid ? (
              "Paid"
            ) : requestInvoiceMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" /> Requesting…
              </>
            ) : isInvoiceAlreadyRequested ? (
              "Invoice requested"
            ) : (
              "Request Invoice"
            )}
          </Button>
        </div>
      )}

      <WorkerRequestsSection
        title="Accepted"
        description="Workers who accepted your invite. You can reject specific dates they accepted, or reject them for the entire shift."
        query={acceptedQ}
        emptyMessage="No accepted requests."
        renderRow={(req) => (
          <AcceptedRequestRow
            key={req.id}
            request={req}
            listBusy={listBusy}
            onReject={rejectWorker}
            pendingRejectKey={pendingRejectKey}
            onViewProfile={openProfile}
          />
        )}
      />

      {!isFinalInvited && (
        <WorkerRequestsSection
          title="Previously rejected"
          description="Workers you rejected before. You can invite them again on selected dates (added to your final invite), or reject the entire shift again."
          query={prevRejectedQ}
          emptyMessage="No previously rejected workers."
          renderRow={(req) => {
            const slug = getRequestWorkerSlug(req);
            const draftedEntry = slug
              ? (finalDraftByShift[req?.shiftIndex ?? 0] || []).find(
                  (x) => x.workerSlug === slug
                )
              : null;
            return (
              <PreviouslyRejectedRequestRow
                key={req.id}
                request={req}
                listBusy={listBusy}
                onReject={rejectWorker}
                onAddToFinalDraft={addToFinalDraft}
                pendingRejectKey={pendingRejectKey}
                draftedDates={draftedEntry?.availableDates || []}
                onViewProfile={openProfile}
              />
            );
          }}
        />
      )}

      <WorkerRequestsSection
        title="Rejected (did not accept)"
        description="Workers who did not accept your invite. No actions are available here."
        query={rejectedQ}
        emptyMessage="No workers in this category."
        renderRow={(req) => (
          <ReadOnlyRequestRow
            key={req.id}
            request={req}
            tone="muted"
            onViewProfile={openProfile}
          />
        )}
      />

      <WorkerRequestsSection
        title="Invited"
        description="Workers you have invited from this review step (after you send final invites, they appear here)."
        query={invitedQ}
        emptyMessage="No invited workers in this list yet."
        renderRow={(req) => (
          <ReadOnlyRequestRow
            key={req.id}
            request={req}
            tone="light"
            onViewProfile={openProfile}
          />
        )}
      />

      {!isFinalInvited && (
        <>
          <div className="d-grid d-md-flex justify-content-md-end mt-4 mb-2">
            <Button
              color="primary"
              size="lg"
              className="px-4"
              disabled={inviteMutation.isPending || !canSendFinalInvites}
              onClick={() => setInviteConfirmOpen(true)}
            >
              {inviteMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" /> Sending…
                </>
              ) : (
                "Send final invites"
              )}
            </Button>
          </div>

          <Modal
            isOpen={inviteConfirmOpen}
            toggle={() =>
              !inviteMutation.isPending && setInviteConfirmOpen(false)
            }
          >
            <ModalHeader
              toggle={() =>
                !inviteMutation.isPending && setInviteConfirmOpen(false)
              }
            >
              Confirm final invites
            </ModalHeader>
            <ModalBody>
              <Alert color="warning" className="mb-0">
                <strong>Important:</strong> This is your final invite step.
                After you send these invites, you <strong>cannot</strong> send
                any more invites for this booking. Make sure your final invite
                draft includes everyone you want to invite with the correct
                dates.
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button
                color="light"
                disabled={inviteMutation.isPending}
                onClick={() => setInviteConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={inviteMutation.isPending}
                onClick={() => sendFinalInvites()}
              >
                {inviteMutation.isPending ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Sending…
                  </>
                ) : (
                  "Yes, send final invites"
                )}
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}

      <Modal
        isOpen={isProfileOpen}
        toggle={() => setIsProfileOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setIsProfileOpen(false)}>
          Worker profile
        </ModalHeader>
        <ModalBody>
          {profileUser ? <WorkerMiniProfile user={profileUser} /> : null}
        </ModalBody>
      </Modal>
    </>
  );
}
